import React, { useEffect, useState, useCallback, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard, Users, ClipboardList, Phone, Calendar,
  Settings, ChevronDown, LogOut, Bell, Check, MapPin,
  PhoneForwarded, Flag, AlertCircle, RefreshCw,
  PhoneMissed, CheckSquare, Brain, Inbox,
  PhoneOff, Megaphone, Activity, History,
  HeartPulse, Search, Sparkles,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useModuleAccess } from '../auth/PermissionGate';
import { useCallStream } from '../../hooks/useCallStream';
import { LiveCallOverlay } from './LiveCallOverlay';
import { DailyPulseSheet } from './DailyPulseSheet';
import { TimeClockMenuItem } from './TimeClockMenuItem';
import api from '../../lib/api';
import { ghlApi } from '../../lib/ghlApi';
import { customLinksApi, resolveCustomLinkUrl } from '../../lib/customLinksApi';
import * as LucideIcons from 'lucide-react';
import { toast } from 'sonner';

/**
 * Primary Header — Modern SaaS Masthead.
 *
 * Direction: Linear/Vercel style. Deep near-black canvas, generous whitespace,
 * a single bright white active pill (high-contrast, always readable), quiet
 * glassy secondary surfaces (search, location), and no gradients or noise.
 * All typography Inter with tabular numerals.
 *
 * The active nav pill is intentionally *neutral bright* — module wayfinding
 * happens exclusively on the SecondaryHeader below, keeping this bar clean.
 */

const primaryNavItems = [
  { path: '/dashboard', label: 'Stats', icon: LayoutDashboard, module: 'stats' },
  { path: '/leads', label: 'Leads', icon: Users, badge: null, module: 'leads' },
  { path: '/patients', label: 'Patients', icon: HeartPulse, module: 'patients' },
  { path: '/front-desk-tasks', label: 'Front Desk', icon: ClipboardList, module: 'front_desk_tasks' },
  { path: '/call-tracker', label: 'Call Tracker', icon: Phone, module: 'call_tracker' },
  { path: '/inbox', label: 'Inbox', icon: Inbox, module: 'inbox' },
  { path: '/scheduling', label: 'Scheduling', icon: Calendar, module: 'scheduling' },
];

export function PrimaryHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, locations, currentLocation, setCurrentLocation, logout } = useAuth();
  const hasModuleAccess = useModuleAccess();

  const isEmbedded = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('embedded') === '1') {
      sessionStorage.setItem('embedded', '1');
      return true;
    }
    if (sessionStorage.getItem('embedded') === '1') return true;
    try { return window.self !== window.top; } catch { return true; }
  }, []);

  const readFromStorage = () => {
    try {
      const raw = localStorage.getItem('leads_metrics');
      if (!raw) return null;
      const m = JSON.parse(raw);
      const v = m?.openLeads;
      return Number.isFinite(v) ? v : null;
    } catch { return null; }
  };

  const [openLeadsCount, setOpenLeadsCount] = useState(() => readFromStorage());

  useEffect(() => {
    const handleUpdate = (e) => {
      if (e?.detail && Number.isFinite(e.detail.openLeads)) {
        setOpenLeadsCount(e.detail.openLeads);
        return;
      }
      setOpenLeadsCount(readFromStorage());
    };
    window.addEventListener('leads_metrics_updated', handleUpdate);
    return () => window.removeEventListener('leads_metrics_updated', handleUpdate);
  }, []);

  const [notifications, setNotifications] = useState({ items: [], total: 0 });
  const [, setLiveCallCount] = useState(0);
  const [callsMuted, setCallsMuted] = useState(() => localStorage.getItem('call_notifications_muted') === 'true');
  const [inboxEnabled, setInboxEnabled] = useState(true);
  const [patientsEnabled, setPatientsEnabled] = useState(true);
  const [patientsModuleScope, setPatientsModuleScope] = useState('all');
  const [patientsModuleLocationIds, setPatientsModuleLocationIds] = useState([]);
  const [changelogEnabled, setChangelogEnabled] = useState(false);
  const [unreadChangelog, setUnreadChangelog] = useState(0);
  const [unreadInbox, setUnreadInbox] = useState(0);
  const [navOrder, setNavOrder] = useState(() => {
    try { const cached = localStorage.getItem('nav_order'); return cached ? JSON.parse(cached) : []; } catch { return []; }
  });
  const [customLinks, setCustomLinks] = useState([]);

  // Fetch custom links visible to the current user for the current location.
  // Re-runs when the location switcher changes so location-scoped links
  // appear/disappear immediately.
  useEffect(() => {
    let alive = true;
    customLinksApi.listForUser(currentLocation?.id).then((r) => {
      if (!alive) return;
      setCustomLinks(Array.isArray(r.data?.links) ? r.data.links : []);
    }).catch(() => { if (alive) setCustomLinks([]); });
    const onUpdate = () => {
      customLinksApi.listForUser(currentLocation?.id).then((r) => {
        if (!alive) return;
        setCustomLinks(Array.isArray(r.data?.links) ? r.data.links : []);
      }).catch(() => {});
    };
    window.addEventListener('custom-links:updated', onUpdate);
    return () => { alive = false; window.removeEventListener('custom-links:updated', onUpdate); };
  }, [currentLocation?.id]);

  // Extracted so we can call it on mount AND on the `app-settings:updated`
  // event so toggling the Patient module in Settings instantly refreshes the
  // nav without needing a full-page refresh.
  const fetchAppSettings = React.useCallback(() => {
    api.get('/auth/app-settings').then(r => {
      if (r.data?.inbox_enabled === false) setInboxEnabled(false);
      else setInboxEnabled(true);
      setPatientsEnabled(r.data?.patients_enabled !== false);
      setPatientsModuleScope(r.data?.patients_module_scope || 'all');
      setPatientsModuleLocationIds(Array.isArray(r.data?.patients_module_location_ids) ? r.data.patients_module_location_ids : []);
      if (r.data?.changelog_enabled === true) setChangelogEnabled(true);
      else setChangelogEnabled(false);
      if (Array.isArray(r.data?.nav_order) && r.data.nav_order.length > 0) {
        setNavOrder(r.data.nav_order);
        localStorage.setItem('nav_order', JSON.stringify(r.data.nav_order));
      } else if (navOrder.length > 0) {
        setNavOrder([]);
        localStorage.removeItem('nav_order');
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAppSettings();
    // Live-refresh when Settings toggles the Patient / Inbox / changelog modules
    const onSettingsUpdate = () => fetchAppSettings();
    window.addEventListener('app-settings:updated', onSettingsUpdate);
    return () => window.removeEventListener('app-settings:updated', onSettingsUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAppSettings]);

  useEffect(() => {
    if (user?.id) {
      api.get(`/changelog/unread-count?user_id=${user.id}`).then(r => {
        setUnreadChangelog(r.data?.unread || 0);
      }).catch(() => {});
    }
    if (currentLocation?.id) {
      api.get('/inbox/unread-count', { params: { location_id: currentLocation.id } }).then(r => {
        setUnreadInbox(r.data?.unread || 0);
      }).catch(() => {});
    }
  }, [currentLocation?.id]);

  const lastEventUpdateRef = useRef(0);
  useEffect(() => {
    const handler = (e) => {
      setUnreadInbox(e.detail?.unread || 0);
      lastEventUpdateRef.current = Date.now();
    };
    window.addEventListener('inbox_unread_updated', handler);
    return () => window.removeEventListener('inbox_unread_updated', handler);
  }, []);

  useEffect(() => {
    if (!currentLocation?.id) return;
    const refresh = () => {
      if (Date.now() - lastEventUpdateRef.current < 20000) return;
      api.get('/inbox/unread-count', { params: { location_id: currentLocation.id } })
        .then(r => setUnreadInbox(r.data?.unread || 0))
        .catch(() => {});
    };
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [currentLocation?.id]);

  const toggleCallMute = useCallback(() => {
    const next = !callsMuted;
    setCallsMuted(next);
    localStorage.setItem('call_notifications_muted', String(next));
    toast.success(next ? 'Call notifications muted' : 'Call notifications enabled');
  }, [callsMuted]);

  const handleCallEvent = useCallback((call) => {
    if (call.location_id && currentLocation?.id && call.location_id !== currentLocation.id) return;
    if (call.type === 'call_inprogress') return;
    if (call.type === 'call_completed') setLiveCallCount(c => c + 1);
  }, [currentLocation?.id]);

  const { connected: liveConnected, liveCount, liveCalls } = useCallStream(currentLocation?.id, handleCallEvent);

  const loadNotifications = useCallback(() => {
    if (!currentLocation?.id) return;
    api.get(`/auth/notifications?location_id=${currentLocation.id}`)
      .then(r => setNotifications(r.data || { items: [], total: 0 }))
      .catch(() => {});
  }, [currentLocation?.id]);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 120000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const NOTIF_ICONS = {
    unclassified_calls: Phone,
    pending_followups: AlertCircle,
    pending_winbacks: PhoneForwarded,
    reported_calls: Flag,
    missed_calls: PhoneMissed,
    overdue_tasks: CheckSquare,
    ai_sanity_alerts: Brain,
    new_leads_today: Users,
    unassigned_leads: Users,
    stale_leads: Users,
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/' || location.pathname === '/dashboard';
    if (path === '/scheduling') return ['/scheduling', '/availability', '/overrides', '/booking-logs', '/scheduling-analytics', '/scheduling-activity-log'].some(p => location.pathname === p);
    if (path === '/admin') return location.pathname.startsWith('/admin');
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => { logout(); navigate('/login'); };
  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

  const prefetchTimers = useRef({});
  const prefetchedRef = useRef(new Set());
  const handleNavHover = useCallback((path) => {
    if (prefetchedRef.current.has(path)) return;
    const locId = currentLocation?.id;
    if (!locId) return;
    prefetchTimers.current[path] = setTimeout(() => {
      prefetchedRef.current.add(path);
      if (path === '/call-tracker') ghlApi.getCalls(locId, { days: 7, limit: 50 }).catch(() => {});
      else if (path === '/scheduling') {
        const tz = currentLocation?.timezone || 'America/Toronto';
        const today = new Date().toISOString().slice(0, 10);
        ghlApi.getCalendarBulkDay(locId, today, tz).catch(() => {});
      }
      else if (path === '/leads') ghlApi.getCachedLeads(locId).catch(() => {});
      else if (path === '/dashboard') ghlApi.getHqFormStats(locId, 7).catch(() => {});
      else if (path === '/front-desk-tasks') ghlApi.getFrontDeskTasks(locId).catch(() => {});
    }, 150);
  }, [currentLocation?.id, currentLocation?.timezone]);

  const handleNavLeave = useCallback((path) => {
    if (prefetchTimers.current[path]) {
      clearTimeout(prefetchTimers.current[path]);
      delete prefetchTimers.current[path];
    }
  }, []);

  useEffect(() => { prefetchedRef.current.clear(); }, [currentLocation?.id]);

  // Patient Profile module gate — respects the "scope" configuration set in
  // Settings → Modules. When scope is "specific", the module only shows for
  // locations explicitly enabled in the allowlist.
  const patientsModuleEnabledHere = (() => {
    if (!patientsEnabled) return false;
    if (patientsModuleScope !== 'specific') return true;
    return patientsModuleLocationIds.includes(currentLocation?.id);
  })();

  // Nav filter + reorder
  const navItems = (() => {
    const filtered = primaryNavItems.filter(item => {
      if (item.path === '/inbox' && !inboxEnabled) return false;
      if (item.path === '/inbox' && inboxEnabled) return true;
      if (item.path === '/patients' && !patientsModuleEnabledHere) return false;
      if (item.path === '/patients' && patientsModuleEnabledHere) return true;
      return !item.module || hasModuleAccess(item.module);
    });
    if (navOrder.length > 0) {
      filtered.sort((a, b) => {
        const ai = navOrder.indexOf(a.path);
        const bi = navOrder.indexOf(b.path);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
    }
    return filtered;
  })();

  // Live ticking date + time for the Daily Pulse chip
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(interval);
  }, []);
  const dateLabel = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const timeLabel = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  const [pulseOpen, setPulseOpen] = useState(false);
  const totalPulse = (notifications.total || 0) + (unreadInbox || 0);

  const isMac = React.useMemo(() => {
    if (typeof navigator === 'undefined') return true;
    return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
  }, []);

  return (
    <>
      <header
        className="sticky top-0 z-50 w-full bg-slate-950"
        data-testid="primary-header"
        style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
      >
        <div className="flex items-center h-[60px] px-4 gap-2 border-b border-white/5">
          {/* ── Daily Pulse chip ─────────────────────────── */}
          <div className="flex items-center mr-4 flex-shrink-0">
            {/* Daily Pulse — clickable pill with live time and counter */}
            <button
              type="button"
              onClick={() => setPulseOpen(true)}
              className="group hidden md:flex items-center gap-2 h-9 pl-2.5 pr-3 rounded-lg bg-teal-500/10 hover:bg-teal-500/15 ring-1 ring-teal-400/25 hover:ring-teal-400/50 transition-all"
              data-testid="daily-pulse-btn"
              title="Open Daily Pulse"
            >
              <Sparkles size={12} strokeWidth={2.25} className="text-teal-400 shrink-0" />
              <div className="flex flex-col leading-none text-left">
                <span className="text-[8.5px] font-bold uppercase tracking-[0.16em] text-slate-500 group-hover:text-teal-300 transition-colors">
                  Daily Pulse
                </span>
                <span className="mt-1 text-[12px] font-semibold text-white tabular-nums tracking-tight">
                  {dateLabel} · {timeLabel}
                </span>
              </div>
              {totalPulse > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-md bg-rose-500 text-white text-[10px] font-bold tabular-nums shadow-[0_0_0_1px_rgba(244,63,94,0.4)]"
                  data-testid="daily-pulse-count"
                >
                  {totalPulse > 99 ? '99+' : totalPulse}
                </span>
              )}
            </button>
          </div>

          {/* ── Nav row (Linear-style pill tabs) ───────────────── */}
          <nav
            className="flex items-center gap-0.5 flex-1 overflow-x-auto scrollbar-hide"
            data-testid="primary-nav"
          >
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const badgeValue = item.path === '/leads' ? openLeadsCount : item.badge;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                  onMouseEnter={() => handleNavHover(item.path)}
                  onMouseLeave={() => handleNavLeave(item.path)}
                  className={cn(
                    'group relative flex items-center gap-1.5 rounded-lg px-3 h-9 text-[12.5px] whitespace-nowrap transition-all duration-150 tracking-tight',
                    active
                      ? 'bg-white text-slate-950 font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.35),0_0_0_1px_rgba(255,255,255,0.06)]'
                      : 'text-slate-300 hover:text-white hover:bg-white/[0.14] font-medium'
                  )}
                >
                  <Icon size={14} strokeWidth={active ? 2.25 : 1.75} className={cn('transition-colors', active ? 'text-slate-900' : 'text-slate-400 group-hover:text-white')} />
                  <span>{item.label}</span>

                  {item.path === '/call-tracker' && liveConnected && (
                    <span className="relative flex h-1.5 w-1.5 ml-0.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    </span>
                  )}
                  {item.path === '/call-tracker' && liveCount > 0 && (
                    <span className={cn(
                      'ml-0.5 inline-flex items-center h-[16px] px-1.5 rounded-md text-[9.5px] font-bold uppercase tracking-wider',
                      active ? 'bg-emerald-100 text-emerald-800' : 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/25'
                    )}>
                      Live · {liveCount}
                    </span>
                  )}
                  {item.path === '/leads' && badgeValue !== null && badgeValue !== undefined && badgeValue !== '' && (
                    <span className={cn(
                      'ml-0.5 inline-flex items-center justify-center h-[18px] min-w-[18px] px-1.5 rounded-md text-[10px] font-bold tabular-nums',
                      active ? 'bg-slate-950 text-white' : 'bg-white/[0.08] text-slate-300 ring-1 ring-white/[0.06]'
                    )}>
                      {badgeValue}
                    </span>
                  )}
                  {item.path === '/inbox' && unreadInbox > 0 && (
                    <span className={cn(
                      'ml-0.5 inline-flex items-center justify-center h-[18px] min-w-[18px] px-1.5 rounded-md text-[10px] font-bold text-white tabular-nums',
                      active ? 'bg-rose-500' : 'bg-rose-500 shadow-[0_0_0_1px_rgba(244,63,94,0.3)]'
                    )}>
                      {unreadInbox > 99 ? '99+' : unreadInbox}
                    </span>
                  )}
                </NavLink>
              );
            })}

            {/* Custom Links — admin-managed extra top-nav tabs */}
            {customLinks.map((link) => {
              const CustomIcon = (LucideIcons[link.icon] && typeof LucideIcons[link.icon] === 'function')
                ? LucideIcons[link.icon]
                : LucideIcons.Link;
              const active = location.pathname === `/custom/${link.id}`;
              const onClickNewTab = (e) => {
                if (link.open_mode !== 'new_tab') return;
                e.preventDefault();
                const url = resolveCustomLinkUrl(link.url, { location: currentLocation, user });
                window.open(url, '_blank', 'noopener,noreferrer');
              };
              return (
                <NavLink
                  key={`custom-${link.id}`}
                  to={link.open_mode === 'new_tab' ? '#' : `/custom/${link.id}`}
                  onClick={onClickNewTab}
                  data-testid={`nav-custom-${link.id}`}
                  className={cn(
                    'group relative flex items-center gap-1.5 rounded-lg px-3 h-9 text-[12.5px] whitespace-nowrap transition-all duration-150 tracking-tight',
                    active
                      ? 'bg-white text-slate-950 font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.35),0_0_0_1px_rgba(255,255,255,0.06)]'
                      : 'text-slate-300 hover:text-white hover:bg-white/[0.14] font-medium'
                  )}
                  title={link.title}
                >
                  <CustomIcon size={14} strokeWidth={active ? 2.25 : 1.75}
                    className={cn('transition-colors', active ? 'text-slate-900' : 'text-slate-400 group-hover:text-white')} />
                  <span>{link.title}</span>
                  {link.open_mode === 'new_tab' && (
                    <LucideIcons.ExternalLink size={10}
                      className={cn('ml-0.5', active ? 'text-slate-500' : 'text-slate-400')} />
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* ── Right cluster ──────────────────────────────────── */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Search — collapsible glassy pill (expands on hover) */}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('global-patient-search:open'))}
              className="group hidden md:inline-flex items-center h-9 rounded-lg bg-violet-500/10 hover:bg-violet-500/15 ring-1 ring-violet-400/25 hover:ring-violet-400/50 transition-all"
              title="Search patients (Cmd/Ctrl+K)"
              data-testid="global-search-btn"
            >
              <span className="flex items-center justify-center h-9 w-9 shrink-0">
                <Search size={14} strokeWidth={2.25} className="text-violet-300 group-hover:text-violet-200 transition-colors" />
              </span>
              <span className="max-w-0 group-hover:max-w-[220px] overflow-hidden transition-[max-width] duration-300 ease-out whitespace-nowrap">
                <span className="flex items-center gap-2 pr-2.5">
                  <span className="text-[12px] font-medium text-slate-300 group-hover:text-white transition-colors">
                    Search patient
                  </span>
                  <kbd className="inline-flex items-center gap-0.5 text-[9.5px] font-bold text-slate-400 bg-slate-900/80 ring-1 ring-white/[0.06] px-1.5 py-0.5 rounded tabular-nums">
                    {isMac ? (<><span className="text-[10px] leading-none">⌘</span>K</>) : (<>Ctrl K</>)}
                  </kbd>
                </span>
              </span>
            </button>

            {/* Location switcher — glassy indigo pill */}
            {isEmbedded ? (
              <div className="h-9 px-3 flex items-center gap-2 rounded-lg bg-indigo-500/10 ring-1 ring-indigo-400/25" data-testid="location-selector-static">
                <MapPin size={13} strokeWidth={2} className="text-indigo-300 shrink-0" />
                <div className="flex flex-col leading-none items-start">
                  <span className="text-[8.5px] font-bold uppercase tracking-[0.16em] text-indigo-300/70">Location</span>
                  <span className="mt-1 text-[12px] font-semibold text-white max-w-[140px] truncate">{currentLocation?.name || '—'}</span>
                </div>
              </div>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="h-9 px-3 flex items-center gap-2 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/15 ring-1 ring-indigo-400/25 hover:ring-indigo-400/50 transition-all"
                    data-testid="location-selector"
                  >
                    <MapPin size={13} strokeWidth={2} className="text-indigo-300 shrink-0" />
                    <div className="flex flex-col leading-none items-start">
                      <span className="text-[8.5px] font-bold uppercase tracking-[0.16em] text-indigo-300/70">Location</span>
                      <span className="mt-1 text-[12px] font-semibold text-white max-w-[120px] truncate">{currentLocation?.name || 'Select'}</span>
                    </div>
                    <ChevronDown size={11} className="text-indigo-300/60 ml-0.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60 shadow-xl border border-slate-200">
                  <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Switch Location</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {locations.length === 0 ? (
                    <DropdownMenuItem disabled><span className="text-slate-400">No locations available</span></DropdownMenuItem>
                  ) : (
                    locations.map((loc) => (
                      <DropdownMenuItem key={loc.id} onClick={() => setCurrentLocation(loc)} className="cursor-pointer" data-testid={`location-option-${loc.code}`}>
                        <div className="flex items-center justify-between w-full">
                          <span className="text-[13px]">{loc.name}</span>
                          {currentLocation?.id === loc.id && <Check size={14} className="text-teal-600" />}
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                  {user?.is_super_admin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin/locations')} className="cursor-pointer text-teal-700">Manage Locations</DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Icon strip — compact, glassy on hover */}
            <span className="w-px h-6 bg-white/25 mx-1.5" aria-hidden />

            <IconBtn onClick={() => window.location.reload()} title="Refresh page" testid="page-refresh-btn">
              <RefreshCw size={13} strokeWidth={2} className="text-slate-300 group-hover:text-white transition-colors" />
            </IconBtn>

            <IconBtn
              onClick={toggleCallMute}
              title={callsMuted ? 'Call notifications muted — click to unmute' : 'Mute call notifications'}
              testid="call-mute-btn"
              active={callsMuted}
            >
              {callsMuted
                ? <PhoneOff size={13} strokeWidth={2} className="text-rose-400 group-hover:text-rose-300 transition-colors" />
                : <Phone size={13} strokeWidth={2} className="text-slate-300 group-hover:text-white transition-colors" />
              }
            </IconBtn>

            {(user?.is_super_admin || hasModuleAccess('settings')) && (
              <IconBtn onClick={() => navigate('/admin/settings')} title="Admin settings" testid="admin-settings-btn" active={isActive('/admin')}>
                <Settings size={13} strokeWidth={2} className={cn('transition-colors', isActive('/admin') ? 'text-white' : 'text-slate-300 group-hover:text-white')} />
              </IconBtn>
            )}

            <span className="w-px h-6 bg-white/25 mx-1.5" aria-hidden />

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="h-9 gap-2 pl-1 pr-2 rounded-lg hover:bg-white/[0.14] transition-colors flex items-center" data-testid="user-menu">
                  <Avatar className="h-7 w-7 ring-2 ring-white/10">
                    <AvatarFallback className="bg-gradient-to-br from-teal-500 to-cyan-600 text-white text-[10px] font-bold">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:flex flex-col leading-none items-start">
                    <span className="text-[8.5px] font-bold uppercase tracking-[0.16em] text-slate-500">Signed in</span>
                    <span className="mt-1 text-[12px] font-semibold text-white max-w-[80px] truncate">{user?.name?.split(' ')[0]}</span>
                  </div>
                  <ChevronDown size={11} className="text-slate-500 ml-0.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 shadow-xl border border-slate-200">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-semibold text-slate-900 text-[13px]">{user?.name}</span>
                    <span className="text-[11px] text-slate-500 font-normal mt-0.5">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/activity-history')} className="cursor-pointer" data-testid="activity-history-menu-item">
                  <History size={14} className="mr-2 text-indigo-500" />
                  <span className="flex-1 text-[13px]">Activity History</span>
                </DropdownMenuItem>
                {changelogEnabled && (
                  <DropdownMenuItem onClick={() => navigate('/changelog')} className="cursor-pointer" data-testid="changelog-menu-item">
                    <Megaphone size={14} className="mr-2 text-teal-500" />
                    <span className="flex-1 text-[13px]">What&apos;s New</span>
                    {unreadChangelog > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded bg-teal-500 text-white text-[10px] font-bold tabular-nums">
                        {unreadChangelog > 9 ? '9+' : unreadChangelog}
                      </span>
                    )}
                  </DropdownMenuItem>
                )}
                <TimeClockMenuItem />
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="logout-menu-item">
                  <LogOut size={14} className="mr-2" /> <span className="text-[13px]">Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <LiveCallOverlay liveCalls={liveCalls} />
      <DailyPulseSheet open={pulseOpen} onOpenChange={setPulseOpen} />
    </>
  );
}

// ── Compact icon button ────────────────────────────────────────────
function IconBtn({ children, onClick, title, testid, active, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={cn(
        'group relative h-9 w-9 rounded-lg flex items-center justify-center transition-colors',
        active ? 'bg-white/[0.14]' : 'hover:bg-white/[0.14]'
      )}
    >
      {children}
      {badge > 0 && (
        <span className="absolute top-1 right-1 min-w-[14px] h-[14px] px-[3px] bg-teal-500 text-white text-[8.5px] font-bold rounded-md flex items-center justify-center ring-2 ring-slate-950 tabular-nums">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </button>
  );
}

export default PrimaryHeader;
